import { useState, useRef, useEffect } from 'react';
import { Eye } from 'lucide-react';

interface HoverPreviewProps {
  htmlContent: string;
  className?: string;
  iconSize?: number;
}

export const HoverPreview = ({ 
  htmlContent,
  className = "",
  iconSize = 16 
}: HoverPreviewProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const hoverRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updatePosition = () => {
    if (!hoverRef.current || !dialogRef.current) return;

    const triggerRect = hoverRef.current.getBoundingClientRect();
    const dialogRect = dialogRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Get actual dialog dimensions (fallback to defaults if not rendered yet)
    const dialogWidth = dialogRect.width || 384; // 384px = w-96
    const dialogHeight = dialogRect.height || 200; // fallback height
    
    // Default position: below and to the right of the trigger
    let top = triggerRect.bottom + 8;
    let left = triggerRect.left;

    // Adjust if dialog would go off-screen horizontally
    if (left + dialogWidth > viewportWidth) {
      left = Math.max(8, viewportWidth - dialogWidth - 8);
    }

    // Adjust if dialog would go off-screen vertically
    if (top + dialogHeight > viewportHeight) {
      // Try positioning above the trigger
      const topAbove = triggerRect.top - dialogHeight - 8;
      if (topAbove >= 8) {
        top = topAbove;
      } else {
        // If can't fit above either, position to fit in viewport
        top = Math.max(8, viewportHeight - dialogHeight - 8);
      }
    }

    setPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsVisible(true);
    // Update position after a small delay to ensure dialog is rendered
    setTimeout(() => {
      updatePosition();
    }, 10);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  };

  const handleDialogMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleDialogMouseLeave = () => {
    setIsVisible(false);
  };

  // Update position when window resizes
  useEffect(() => {
    if (isVisible) {
      const handleResize = () => updatePosition();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={hoverRef}
        className={`inline-flex items-center justify-center w-10 h-6 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-700 cursor-pointer transition-colors duration-200 ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Eye size={iconSize} />
      </div>

      {isVisible && (
        <div
          ref={dialogRef}
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-w-[600px] w-[600px] max-h-[600px] overflow-y-auto"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          onMouseEnter={handleDialogMouseEnter}
          onMouseLeave={handleDialogMouseLeave}
        >
          <div className="p-4">
            <div className="prose prose-sm max-w-none">
              <div 
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                className="text-sm leading-relaxed"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};